<script>
import { mapGetters, mapActions } from 'vuex';
import DocumentsTitleUpload from '@/V2/documents/DocumentsTitleUpload';
import CustomerFraudDocuments from '@/V2/customers/fraud/CustomerFraudDocuments.vue';
import CustomerFraudSummary from '@/V2/customers/fraud/CustomerFraudSummary.vue';
import CustomerComplianceSummary from '@/V2/customers/business-compliance-agent/CustomerComplianceSummary.vue';
import CustomerFraudUploading from '@/V2/customers/fraud/CustomerFraudUploading.vue';
import { hasAgentRunInProgress } from '@/utils/ai-agents';
import { getTopRiskLevel } from '@/constants/riskLevels';
import { getLatestBusinessComplianceAgentRun } from '@/V2/customers/business-compliance-agent/utils';
import DailyBalanceGraphContainerExpandable from '@/V2/credit/charts/daily-balance-graph/DailyBalanceGraphContainerExpandable.vue';
import { RUN_STATUS } from '@/constants/analyst-constants';
import { $event } from '@/bus';

export default {
  components: {
    DocumentsTitleUpload,
    CustomerFraudDocuments,
    CustomerFraudSummary,
    CustomerComplianceSummary,
    CustomerFraudUploading,
    DailyBalanceGraphContainerExpandable,
  },
  computed: {
    ...mapGetters([
      'getCachedCustomerReviewAgentRunsForCustomerUUID',
      'getCachedDocumentReviewAgentRunsForCustomerUUID',
      'getCachedBusinessComplianceAgentRunsForCustomerUUID',
      'someDocumentUploadingOrProcessing',
      'getCachedFraudSummary',
      'canViewBusinessComplianceAgent',
      'canUseCredit',
      'unifiedAgentEnabled',
      'canViewCustomerFraudReasoningAgent',
    ]),
    showDocumentsUploadingComponent() {
      return this.someDocumentUploadingOrProcessing;
    },
    fraudSummary() {
      return this.getCachedFraudSummary(this.$route.params.customer_id);
    },
    fraudSummaryIsLoading() {
      return typeof this.fraudSummary === 'undefined';
    },
    riskLevel() {
      return getTopRiskLevel(this.fraudSummary);
    },
    latestCustomerReviewAgentRun() {
      const runs = this.getCachedCustomerReviewAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      return runs?.length > 0 ? runs[runs.length - 1] : null;
    },
    latestBusinessComplianceAgentRun() {
      const runs = this.getCachedBusinessComplianceAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      return getLatestBusinessComplianceAgentRun(runs);
    },
    customerHasDocuments() {
      return (
        this.fraudSummary?.aggregated_risk_counts &&
        Object.values(this.fraudSummary?.aggregated_risk_counts).some(
          (value) => value > 0,
        )
      );
    },
    anyCustomerReviewAgentIsRunning() {
      const runs = this.getCachedCustomerReviewAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      return hasAgentRunInProgress(runs);
    },
    anyDocumentReviewAgentIsRunning() {
      const runs = this.getCachedDocumentReviewAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      return hasAgentRunInProgress(runs);
    },
    complianceStatus() {
      return this.someDocumentUploadingOrProcessing ||
        this.anyDocumentReviewAgentIsRunning ||
        this.anyCustomerReviewAgentIsRunning
        ? RUN_STATUS.RUNNING
        : this.latestBusinessComplianceAgentRun?.status;
    },
    isComplianceRunning() {
      return this.complianceStatus === RUN_STATUS.RUNNING;
    },
    agentIsRunning() {
      return (
        this.anyDocumentReviewAgentIsRunning ||
        this.anyCustomerReviewAgentIsRunning ||
        this.showDocumentsUploadingComponent
      );
    },
  },
  watch: {
    canViewBusinessComplianceAgent: {
      immediate: true,
      handler: function () {
        this.conditionallyRedirectBasedOnFeatureFlags();
      },
    },
    canViewCustomerFraudReasoningAgent: {
      immediate: true,
      handler: function () {
        this.conditionallyRedirectBasedOnFeatureFlags();
      },
    },
    unifiedAgentEnabled: {
      immediate: true,
      handler: function () {
        this.conditionallyRedirectBasedOnFeatureFlags();
      },
    },
  },
  beforeMount() {
    this.conditionallyRedirectBasedOnFeatureFlags();
  },
  methods: {
    ...mapActions([
      'fetchAndCacheBusinessComplianceAgentRunsForCustomerUUID',
      'fetchAndCacheDocumentReviewAgentRunsForCustomerUUID',
      'fetchAndCacheCustomerReviewAgentRunsForCustomerUUID',
    ]),
    conditionallyRedirectBasedOnFeatureFlags() {
      if (this.canViewCustomerFraudReasoningAgent) {
        this.$router.push({
          name: 'customer-fraud-reasoning',
          params: { customer_id: this.$route.params.customer_id },
        });
      } else if (this.unifiedAgentEnabled) {
        this.$router.push({
          name: 'customer-risk-analysis',
          params: { customer_id: this.$route.params.customer_id },
        });
      } else if (
        this.canViewBusinessComplianceAgent === false &&
        this.canUseCredit === false
      ) {
        this.$router.push({
          name: 'customer-fraud-analyst',
          params: { customer_id: this.$route.params.customer_id },
        });
      }
    },
    onUploadDocumentsClick() {
      const documentsTitleUpload = this.$refs.documentsTitleUpload;
      documentsTitleUpload.startUpload();
    },
    onViewFraudButtonClick() {
      this.$router.push({
        name: 'customer-fraud-analyst',
        params: { customer_id: this.$route.params.customer_id },
      });
    },
    onViewComplianceButtonClick() {
      this.$router.push({
        name: 'customer-compliance',
        params: { customer_id: this.$route.params.customer_id },
      });
    },
  },
};
</script>

<template>
  <div
    class="customer-overview i-px-11 i-pt-11 i-pb-11 i-flex"
    data-testid="customer-overview-page"
  >
    <div class="customer-overview-container d-flex flex-column">
      <!-- title with upload -->
      <documents-title-upload
        ref="documentsTitleUpload"
        title="Overview"
        subtitle="Summary of all insights for this customer"
        :expanded="false"
      >
      </documents-title-upload>

      <!-- content -->
      <div class="overview-content d-flex flex-column i-gap-8">
        <customer-fraud-uploading v-if="showDocumentsUploadingComponent" />

        <!-- first row of summaries -->
        <div class="d-flex flex-row i-gap-8">
          <!-- summary -->
          <customer-fraud-summary
            class="summary-card"
            :summary="fraudSummary"
            title="Fraud"
            button-text="View Fraud"
            :latest-customer-run="latestCustomerReviewAgentRun"
            :customer-has-documents="customerHasDocuments"
            :loading="fraudSummaryIsLoading"
            :agent-is-running="agentIsRunning"
            @button-click="onViewFraudButtonClick"
          />

          <!-- compliance summary -->
          <customer-compliance-summary
            v-if="canViewBusinessComplianceAgent"
            class="summary-card"
            :summary="fraudSummary"
            title="Compliance"
            button-text="View Compliance"
            :latest-compliance-run="latestBusinessComplianceAgentRun"
            :loading="fraudSummaryIsLoading"
            :is-compliance-running="isComplianceRunning"
            @button-click="onViewComplianceButtonClick"
          />
        </div>

        <!-- credit chart -->
        <daily-balance-graph-container-expandable v-if="canUseCredit" />

        <!-- documents -->
        <customer-fraud-documents
          :risk-level="riskLevel"
          :fraud-summary="fraudSummary"
          @upload-documents="onUploadDocumentsClick"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.customer-overview {
  overflow-y: auto;

  .customer-overview-container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .summary-card {
    width: 100%;

    :deep(.expandable-container),
    :deep(.content) {
      height: 100% !important;
    }
  }
}
</style>
